import Modal from '@/shared/components/Modal';
import { sand, subheadline, white } from '@/shared/styles/colors';
import styled, { css } from '@emotion/native';
import React, { useRef, useEffect } from 'react';
import CloseGreen from '@/assets/images/CloseGreen.png';
import { BodyText, H2 } from '@/shared/components/StyledText';
import Button from '@/shared/components/Button';
import { format } from 'date-fns';
import { CANCEL_CONSULTATION } from '@/shared/mutations';
import { useMutation } from '@apollo/client';
import useStore from '@/stores/useStore';
import {
  ConsultationCancellationType,
  MutationCancelConsultationArgs,
  MutationResult,
} from '@/shared/types/generated';
import { GET_CONSULTATIONS } from '@/shared/queries';
import ModalWrapper from '@/shared/styles/components/ModalWrapper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { formatCurrency } from '@/shared/utils';

interface CancelSessionModalProps {
  isModalVisible: boolean;
  setIsModalVisible: (isVisible: boolean) => void;
  otherUserName: string;
  consultationDate: Date;
  consultationId?: number;
  stylistId?: number;
  clientId?: number;
  closeParentModal: () => void;
  isOutsideRefundWindow: boolean;
  sessionPrice?: number;
}

const CancelSessionModal = ({
  isModalVisible,
  setIsModalVisible,
  otherUserName,
  consultationDate,
  consultationId,
  stylistId,
  clientId,
  closeParentModal,
  isOutsideRefundWindow,
  sessionPrice,
}: CancelSessionModalProps) => {
  const { setSnackbarState, isStylist } = useStore();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const errorMessage =
    'Failed to cancel session, please try again or contact support';

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const [cancelSession, { loading }] = useMutation<
    MutationResult<'cancelConsultation'>,
    MutationCancelConsultationArgs
  >(CANCEL_CONSULTATION, {
    refetchQueries: [{ query: GET_CONSULTATIONS }],
    onCompleted: () => {
      setSnackbarState({
        message: 'Session canceled',
        open: true,
        severity: 'success',
      });
      setIsModalVisible(false);
      timeoutRef.current = setTimeout(() => {
        closeParentModal();
        timeoutRef.current = null;
      }, 100);
    },
    onError: () => {
      setSnackbarState({
        message: errorMessage,
        open: true,
        severity: 'error',
      });
    },
  });

  const handleCancelSession = () => {
    if (!consultationId || !stylistId || !clientId) {
      setSnackbarState({
        message: errorMessage,
        open: true,
        severity: 'error',
      });
      return;
    }

    cancelSession({
      variables: {
        input: {
          consultationId,
          stylistId,
          clientId,
          cancellationType: ConsultationCancellationType.Cancelled,
          isOutsideRefundWindow,
        },
      },
    });
  };

  const insets = useSafeAreaInsets();
  const CLOSE_BUTTON_TOP_POSITION = 21;

  return (
    <Modal
      isVisible={isModalVisible}
      setIsVisible={setIsModalVisible}
      onDismiss={() => setIsModalVisible(false)}
      onRequestClose={() => setIsModalVisible(false)}
    >
      <ModalWrapper style={containerStyles}>
        <CloseButton
          onPress={() => setIsModalVisible(false)}
          style={{ top: insets.top + CLOSE_BUTTON_TOP_POSITION }}
        >
          <Image source={CloseGreen} />
        </CloseButton>
        <Title>Confirm Cancel </Title>
        <Body>
          Are you sure you want to cancel your upcoming session with{' '}
          {otherUserName}?
        </Body>
        <Date>
          {isOutsideRefundWindow
            ? `${format(consultationDate, 'EEEE MMMM d yyyy')} at ${format(consultationDate, 'h:mma')}`
            : `${format(consultationDate, 'MMM d, yyyy')} at ${format(consultationDate, 'h:mm a')}`}
        </Date>
        {isOutsideRefundWindow && sessionPrice && !isStylist && (
          <WarningText>
            By canceling within 24 hours,{'\n'}your credit card will be charged
            for{'\n'}the full amount: {formatCurrency(sessionPrice)}
          </WarningText>
        )}
        <Button
          onPress={handleCancelSession}
          text="Yes, Cancel Session"
          customStyle={{ marginBottom: 24 }}
          loading={loading}
          disabled={loading}
        />
        <Button
          onPress={() => setIsModalVisible(false)}
          text="No, nevermind"
          variant="secondary"
          customStyle={{ backgroundColor: white }}
          disabled={loading}
        />
      </ModalWrapper>
    </Modal>
  );
};

const containerStyles = css({
  backgroundColor: sand,
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
  paddingHorizontal: 28,
  width: '100%',
  maxWidth: 697,
  maxHeight: 1247,
});

const textAlignStyles = css({
  textAlign: 'center',
});

const Title = styled(H2)({
  marginBottom: 6,
  textTransform: 'uppercase',
  ...textAlignStyles,
});

const Body = styled(BodyText)({
  marginBottom: 20,
  fontSize: 16,
  ...textAlignStyles,
  color: subheadline,
  width: '90%',
  lineHeight: 16,
  letterSpacing: -0.16,
});

const Date = styled(BodyText)({
  marginBottom: 21,
  ...textAlignStyles,
  fontSize: 14,
  fontWeight: 'bold',
  color: subheadline,
});

const WarningText = styled(BodyText)({
  marginBottom: 32,
  ...textAlignStyles,
  fontSize: 16,
  color: subheadline,
  lineHeight: 19.5,
});

const CloseButton = styled.TouchableOpacity({
  position: 'absolute',
  right: 23,
});

const Image = styled.Image({
  width: 24,
  height: 24,
});

export default CancelSessionModal;
